import { useCallback, useEffect, useState } from 'react';
import { useTeams } from '../use-teams';
import { useListMemory, useRememberedString } from '../list-memory';
import {
  leaveBalanceSchema,
  leaveDecisionResultSchema,
  leaveRequestSchema,
  leaveTypeSchema,
  personSchema,
  type LeaveBalance,
  type LeaveType,
  type RequestListScope,
} from '@forecastify/shared';
import { useSearchParams } from 'react-router-dom';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList } from '../permissions-hook';
import { useLiveInvalidation } from '../../live/use-live';
import { LEAVE_TOPICS } from '../../live/topics';
import { useWorkspaceNavigate } from '../../workspace-path';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { useAvatars } from '../use-avatar';
import { useFirstLoad } from '../use-first-load';
import { RequestsScreen, type PersonLook, type RequestSelection } from './requests-screen';
import { NewClaimDialog } from './new-claim-dialog';
import { NewRequestDialog } from './new-request-dialog';
import { isNarrowViewport } from '../narrow';

/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A REQUEST OPENS AS A MODAL — feedback round 4 item 24.                      │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * These screens hard-coded `'drawer'`, which contradicted item 34's own ladder: *a drill-in from a
 * LIST opens as a modal*, which `nextSurface('page')` says and every other list in the product
 * follows. A request is the one detail that opened differently from its siblings, and a drawer is
 * the narrower surface — a leave request's day breakdown and comment thread are exactly what it
 * has least room for.
 *
 * ⚠️ **The drawer stays on a phone**, which the item says in as many words and `narrow.ts`
 * argues: a 960px modal centred in a 390px viewport is a drawer with worse margins.
 */
function openSurface(): RequestMode {
  return isNarrowViewport() ? 'drawer' : 'modal';
}
import { RequestOverlay } from './request-overlay';
import { DecideDialog, type DecideRequest } from './decide-dialog';
import { useDecisionOptions } from './decision-options';
import { settleBody, type DecisionDraft } from './decision-controls';
import type { RequestMode } from './request-surface';
import type { Translate } from '../../i18n';
import { readAll, requestAll } from '../read-all';

/**
 * ⚠️ **The generic codes only.** Every refusal this screen can DO something about gets its own
 * sentence at the call site; this is the tail, and `settings-route.tsx` carries the same three
 * lines for the same reason — one `t()` per branch with a literal key.
 */
function messageFor(t: Translate, code: string): string {
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  return t('error.unavailable');
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Kérelmeim and Kérelmek — C47. One route function, two addresses.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The SCOPE is the whole difference**, and it travels to the server as `?scope=` rather
 * than being applied here: *"my requests"* is a filter the API can express and the client cannot,
 * because a person's own id is the session's rather than a query the browser should assemble.
 */
/**
 * ⚠️⚠️ **THREE SCOPES, AND THE THIRD IS WHY THIS STOPPED BEING A BOOLEAN.** `mine=false` meant
 * *the whole workspace* to somebody holding `leave.view` and *my own reports* to somebody without
 * — one flag with two meanings, chosen by the reader's permissions rather than by the screen they
 * opened. A lead who ALSO held `leave.view` therefore had no way to see just their own queue,
 * which is the screen the product owner asked for.
 */
function useRequests(listScope: RequestListScope) {
  const session = useSession();
  const t = useT();
  /* R-60 · the queues' team filter, narrowing both lists on the server. The personal list has
     none — every row there is the reader's own. */
  const teams = useTeams();
  /* 3 Nov 2026 · remembered per workspace and member (`list-memory.ts`). */
  const memory = useListMemory(listScope === 'mine' ? 'my-requests' : 'requests');
  const [teamId, setTeamId] = useRememberedString(memory, 'teamId');
  const team = listScope === 'mine' || teamId === '' ? '' : `&teamId=${encodeURIComponent(teamId)}`;

  const thisYear = new Date().getFullYear();
  const years = [thisYear - 1, thisYear, thisYear + 1];

  const [year, setYear] = useState(thisYear);
  /**
   * 31 Oct 2026 · ⚠️ **THE LISTS ARE READ BY THE SCREEN NOW, ONE PAGE AT A TIME** — this route held
   * every request and claim the reader could see, 3,276 on UAT. It reads what the screen draws
   * around them, and counts its reloads: `revision` is the lists' `nonce`, so a write or a live
   * change re-reads the page being looked at.
   */
  const [revision, setRevision] = useState(0);
  const [balances, setBalances] = useState<readonly LeaveBalance[]>([]);
  const [types, setTypes] = useState<readonly LeaveType[]>([]);
  /* ⚠️ Only the four fields a face needs — id, name, avatar path, seed. See `PersonLook`. */
  const [people, setPeople] = useState<
    readonly {
      id: string;
      name: string;
      avatarUrl: string | null;
      avatarSeed: string | null;
    }[]
  >([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /**
     * ⚠️ **One row of the list the screen draws, read only for its answer** — a refusal (`all`
     * without `leave.view`) or a failure is this screen's error state, which the screen's own page
     * reads cannot raise: they keep their rows on screen. The rows themselves are the screen's.
     */
    const query = new URLSearchParams();
    query.set('scope', listScope);
    if (team !== '') query.set('teamId', teamId);
    query.set('limit', '1');
    const probe = listScope === 'mine' ? '/leave-requests' : '/requests';

    /**
     * ⚠️⚠️ **ALL AT ONCE — 13 Oct 2026.** They were awaited one after another, so the page waited
     * for the SUM of the round trips where the task list waits for the slowest of its own. None of
     * them reads another's answer; only the first is fatal, and it still is.
     */
    const [response, balanceResponse, typeResponse, peopleResponse] = await Promise.all([
      session.request(`${probe}?${query.toString()}`).catch(() => null),
      /* ⚠️ The queue draws no balance cards. */
      listScope === 'mine'
        ? session.request(`/leave-requests/balances?year=${String(year)}`).catch(() => null)
        : null,
      session.request('/workspace/leave/types').catch(() => null),
      requestAll(session, '/people').catch(() => null),
    ]);

    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
    if (!Array.isArray(body?.data)) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setRevision((current) => current + 1);

    /* ⚠️ Not fatal: the balance panel is a summary of the list below it. */
    if (balanceResponse?.ok === true) {
      const rows = parseList(leaveBalanceSchema, await balanceResponse.json());
      if (rows !== null) setBalances(rows);
    }

    if (typeResponse?.ok === true) {
      const rows = parseList(leaveTypeSchema, await typeResponse.json());
      if (rows !== null) setTypes(rows.filter((row) => row.active));
    }

    /* ⚠️ Not fatal: losing it costs the rows their pictures, not the screen. */
    if (peopleResponse?.ok === true) {
      const rows = parseList(personSchema, await peopleResponse.json());
      if (rows !== null) {
        setPeople(
          rows.map((person) => ({
            id: person.id,
            name: person.displayName,
            avatarUrl: person.avatarUrl,
            avatarSeed: person.avatarSeed,
          })),
        );
      }
    }
  }, [session, listScope, year, team, teamId]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(LEAVE_TOPICS, reload);

  return {
    teams,
    teamId,
    setTeamId,
    t,
    session,
    year,
    years,
    setYear,
    revision,
    balances,
    types,
    people,
    failure,
    loading,
    serverError,
    setServerError,
    toasts,
    setToasts,
    reload,
  };
}

/**
 * The roster as the request lists want it: a name, and a picture when there is one.
 *
 * ⚠️ **`useAvatars` takes the whole list rather than being called per row**, which is the rule
 * `use-avatar.ts` states: a row that appears when a filter clears would change how many hooks a
 * component runs.
 */
function usePeopleLook(
  people: readonly {
    id: string;
    name: string;
    avatarUrl: string | null;
    avatarSeed: string | null;
  }[],
): readonly PersonLook[] {
  const avatars = useAvatars(people.map((person) => person.avatarUrl));
  return people.map((person) => ({
    id: person.id,
    name: person.name,
    avatar: avatars.get(person.avatarUrl ?? ''),
    seed: person.avatarSeed,
  }));
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE DECISION PATH, AND THE IDS ARE SPLIT BY KIND HERE.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The two are separate entities with separate endpoints; one table over both is a rendering
 * decision. This function holds both lists, so it is the one place that can tell a leave request
 * from an overtime claim without putting a discriminator on the wire.
 *
 * ⚠️ **The two answers are SUMMED: one selection, one outcome.** Two toasts for one press would
 * read as two things having happened.
 */
function decide(
  state: ReturnType<typeof useRequests>,
  session: ReturnType<typeof useSession>,
  t: Translate,
  selection: RequestSelection,
  decision: 'approved' | 'rejected',
  /**
   * ⚠⚠️ **EVERY CALLER SENDS ONE NOW — and the row menu could not until `0147`.** Item 31
   * put the settlement on the detail's rail and left the kebab deciding on click, which after
   * `0147` meant the one choice the approver owns — what kind of overtime this was — was
   * reachable only by opening the claim. `decide-dialog.tsx` is the surface that asks.
   *
   * ⚠️ **One answer covers the batch**, because `POST /overtime/decide` takes one settlement and
   * one type for all its ids. Asking once per batch is what the endpoint's shape allows.
   *
   * ⚠️ Absent still means *payout, priced from the calendar* — the server's own default, so a
   * caller that says nothing behaves as approval always has.
   */
  settle?: { settlement: string; creditLeaveTypeId?: string; overtimeTypeId?: string },
  /** ⚠️ The decider's word. `null` rather than an empty string — the endpoint's own shape. */
  note?: string,
): Promise<boolean> {
  /* 26 Oct 2026 · resolves to whether anything landed — the decide dialog closes on `true` only. */
  return (async () => {
    state.setServerError(null);
    const { leaveIds, overtimeIds } = selection;

    const answers = await Promise.all(
      [
        leaveIds.length === 0
          ? null
          : session.request('/leave-requests/decide', {
              method: 'POST',
              /* ⚠️ The leave queue ignores a settlement and always did — it has no such choice. */
              body: JSON.stringify({
                ids: leaveIds,
                decision,
                ...(note === undefined || note === '' ? {} : { note }),
              }),
            }),
        overtimeIds.length === 0
          ? null
          : session.request('/overtime/decide', {
              method: 'POST',
              body: JSON.stringify({
                ids: overtimeIds,
                decision,
                ...settle,
                ...(note === undefined || note === '' ? {} : { note }),
              }),
            }),
      ].map((call) => call?.catch(() => null) ?? Promise.resolve(null)),
    );

    const usable = answers.filter((one) => one?.ok === true);
    if (usable.length === 0) {
      state.setServerError(t('error.unavailable'));
      return false;
    }

    let decided = 0;
    let refused = 0;
    for (const answer of usable) {
      const parsed = leaveDecisionResultSchema.safeParse(await answer?.json());
      if (!parsed.success) continue;
      decided += parsed.data.decided.length;
      refused += parsed.data.refused.length;
    }

    await state.reload();
    state.setToasts((current) => [
      ...current,
      {
        id: `decide-${String(Date.now())}`,
        /* ⚠️ `info` rather than `error`: a partly-applied batch is an OUTCOME, not a failure — the
           refused ids were decided by somebody else first, or lacked a required attachment. */
        variant: refused === 0 ? 'success' : 'info',
        title:
          refused === 0
            ? t('leave.decided', { count: decided })
            : t('leave.decidedSome', { count: decided, refused }),
      },
    ]);
    return true;
  })();
}

/** Kérelmeim — the person's own, with the balance panel and the new-request dialog. */
export function RequestsRoute() {
  const state = useRequests('mine');
  const { t, session } = state;
  const go = useWorkspaceNavigate();
  /* ⚠️ The pictures come through the avatar cache, which fetches each path once for the
     whole app — `use-avatar.ts` argues why a hook cannot be called per row. */
  const look = usePeopleLook(state.people);
  /* ⚠️ P3-9 · a row opens the DRAWER over the list, which is how every other detail in
     the product opens (`task-overlay.tsx`). The page is still an address. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [openMode, setOpenMode] = useState<RequestMode>(openSurface);
  /* ⚠️ The batch the dialog is asking about — `null` closes it. One selection, one answer. */
  const [deciding, setDeciding] = useState<DecideRequest | null>(null);
  /* ⚠️ The same two lists the detail rail reads, from the same hook — `decision-options.ts`. */
  const decisionOptions = useDecisionOptions();
  /* ⚠️ The SAME answer the sidebar gates its row on, read from the same place — two readers of one
     fact, rather than this screen deciding for itself whether the form would work. */
  const canRequest = useSidebarContext()?.view.entitlements.leaveRequest ?? false;
  /* ⚠️ The dialog owns its own draft now — the type, the frame year, the selection and the note
     are its state, and the route only says whether it is open. */
  const [composing, setComposing] = useState(false);
  /**
   * ⚠️⚠️ **THE DAYS THIS PERSON HAS ALREADY ASKED FOR, SO THE CALENDAR CAN REFUSE THEM.** Pending
   * and approved only: a rejected or revoked request books nothing, and greying its days would hide
   * days somebody may legitimately take. The server refuses an overlap with `overlaps` either way —
   * this is so nobody reaches it.
   *
   * 31 Oct 2026 · ⚠️ **Read when the form opens**, every page of the caller's own requests — the
   * list beside it holds one page now. One person's requests, bounded (`read-all.ts`).
   */
  const [taken, setTaken] = useState<readonly { fromDate: string; toDate: string }[]>([]);
  useEffect(() => {
    if (!composing) return;
    const run = { live: true };
    void (async () => {
      const rows = await readAll(session, '/leave-requests?scope=mine', leaveRequestSchema).catch(
        () => null,
      );
      if (!run.live || rows === null) return;
      setTaken(
        rows
          .filter((one) => one.status === 'pending' || one.status === 'approved')
          .map((one) => ({ fromDate: one.fromDate, toDate: one.toDate })),
      );
    })();
    return () => {
      run.live = false;
    };
  }, [composing, session]);
  /* ⚠️ The overtime tab's own composer. Separate state, because the two dialogs are different
     forms over different shapes — a range of days against a frame, and one logged hour. */
  const [claiming, setClaiming] = useState(false);

  /**
   * ⚠️⚠️ **`?new=1` OPENS THE FORM — Home's *New request* sends it, and nothing used to read it.** The
   * button landed on the list and the form never opened. Read once and cleared, as `tasks-route`
   * does, so Back does not reopen it.
   */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('new') === null) return;
    if (canRequest) setComposing(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [params, setParams, canRequest]);

  /**
   * 16 Oct 2026 · Kezdőlap's *Túlóra* card sends `?tab=overtime`, and a claim row adds
   * `&open=<id>` — a claim has no page of its own, so it opens in this screen's drawer. Both read
   * once, then cleared like `?new=1`, so Back does not reopen them.
   *
   * ⚠️ **The tab is read during the first render**, not in the effect: it seeds the screen's own
   * tab state, which takes its initial value once.
   */
  const [initialTab] = useState<'leave' | 'overtime'>(() =>
    params.get('tab') === 'overtime' ? 'overtime' : 'leave',
  );
  useEffect(() => {
    if (params.get('tab') === null && params.get('open') === null) return;
    const open = params.get('open');
    if (open !== null) {
      setOpenId(open);
      setOpenMode(openSurface());
    }
    const next = new URLSearchParams(params);
    next.delete('tab');
    next.delete('open');
    setParams(next, { replace: true });
  }, [params, setParams]);

  return (
    <ScreenState failure={state.failure} onRetry={() => void state.reload()}>
      <RequestsScreen
        listScope="mine"
        initialTab={initialTab}
        loading={state.loading}
        canRequest={canRequest}
        people={look}
        nonce={state.revision}
        balances={state.balances}
        types={state.types}
        serverError={state.serverError}
        handlers={{
          onNew: () => {
            setComposing(true);
          },
          onNewClaim: () => {
            state.setServerError(null);
            setClaiming(true);
          },
          /* ⚠️ P3-9 · only a LEAVE request has a detail screen; a claim is one worklog and one
             multiplier, so its row menu offers the TASK instead. */
          onOpen: (id) => {
            setOpenId(id);
            setOpenMode(openSurface());
          },
          onOpenTask: (key) => {
            go(`/tasks/${key}`);
          },
          /**
           * ⚠️⚠️ **IT ASKS, IT DOES NOT DECIDE.** The kebab's *Elfogádás* used to post an
           * approval the instant it was clicked — no note, no settlement and, after `0147`, no
           * overtime type, which left the approver's one real choice reachable only by opening the
           * request. `decide-dialog.tsx` is the surface; this only carries the selection to it.
           */
          onDecide: (selection, decision) => {
            setDeciding({ selection, decision });
          },
          onRevoke: (request) => {
            void (async () => {
              state.setServerError(null);
              const path =
                request.kind === 'overtime'
                  ? `/overtime/${request.id}/revoke`
                  : `/leave-requests/${request.id}/revoke`;
              const response = await session
                .request(path, {
                  method: 'POST',
                  body: JSON.stringify({ version: request.version }),
                })
                .catch(() => null);
              if (response?.ok !== true) {
                const envelope = (await response?.json().catch(() => null)) as
                  { error?: { code?: string } } | null | undefined;
                state.setServerError(messageFor(t, envelope?.error?.code ?? 'unavailable'));
                return;
              }
              await state.reload();
            })();
          },
        }}
      />

      <NewRequestDialog
        open={composing}
        types={state.types}
        balances={state.balances}
        taken={taken}
        serverError={state.serverError}
        onClose={() => {
          setComposing(false);
        }}
        onSubmit={(draft) => {
          void (async () => {
            state.setServerError(null);
            const response = await session
              .request('/leave-requests', {
                method: 'POST',
                body: JSON.stringify({
                  leaveTypeId: draft.leaveTypeId,
                  year: draft.year,
                  fromDate: draft.fromDate,
                  toDate: draft.toDate,
                  note: draft.note.trim() === '' ? null : draft.note.trim(),
                }),
              })
              .catch(() => null);

            if (response === null) {
              state.setServerError(t('error.unavailable'));
              return;
            }
            if (!response.ok) {
              const body = (await response.json().catch(() => null)) as {
                error?: { code?: string };
              } | null;
              /**
               * ⚠️ **Each refusal keeps its own sentence**, because each has a different next move:
               * change the dates, look at the other request, or talk to an administrator.
               */
              const code = body?.error?.code ?? 'unavailable';
              state.setServerError(
                code === 'overlaps'
                  ? t('leave.error.overlaps')
                  : code === 'backdate_not_allowed'
                    ? t('leave.error.backdate_not_allowed')
                    : code === 'leave_not_managed'
                      ? t('leave.error.leave_not_managed')
                      : code === 'type_inactive'
                        ? t('leave.error.type_inactive')
                        : /* ⚠️ The frame cannot cover the days asked for — `leave-frame.ts`. The
                             calendar greys them, so this is the belt to that pair of braces. */
                          code === 'quota_exceeded'
                          ? t('leave.error.quota_exceeded')
                          : code === 'frame_closed'
                            ? t('leave.error.frame_closed')
                            : messageFor(t, code),
              );
              return;
            }
            /**
             * ┌───────────────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ THE FILES GO UP AFTER THE CREATE, BECAUSE THEY NEED AN ID — 15 Sep.  │
             * └───────────────────────────────────────────────────────────────────────────┘
             *
             * Reported as *"why can I only upload a document for my leave request after
             * submitting?"* The reason a picker could not sit in the form was that a file has to
             * hang off something — true, and answered by staging `File` objects in the browser for
             * the second it takes to POST the request. Same order the task comment composer uses.
             *
             * ⚠️ **Sequential, not `Promise.all`** — each file is scanned by ClamAV, and firing
             * five at one clamd is how a scan times out and refuses a file that was fine.
             *
             * ⚠️ **A failed upload does not fail the REQUEST**, which is already created and
             * visible. The dialog closes either way and the detail screen offers the upload again;
             * treating a refused certificate as a refused request would lose the dates somebody
             * just picked.
             */
            const created = (await response.json().catch(() => null)) as { id?: string } | null;
            if (typeof created?.id === 'string') {
              for (const file of draft.files) {
                const form = new FormData();
                form.set('file', file);
                await session
                  .request(`/files?target=leave_request&targetId=${created.id}`, {
                    method: 'POST',
                    body: form,
                  })
                  .catch(() => null);
              }
            }

            setComposing(false);
            await state.reload();
          })();
        }}
      />

      <NewClaimDialog
        open={claiming}
        serverError={state.serverError}
        onClose={() => {
          setClaiming(false);
        }}
        onSubmit={(draft) => {
          void (async () => {
            state.setServerError(null);
            const response = await session
              .request('/overtime', {
                method: 'POST',
                /* ⚠️ The worklog and an optional note — `0147` took the type out of raising a
                   claim entirely, because the rate is the approver's call. */
                body: JSON.stringify({
                  worklogId: draft.worklogId,
                  ...(draft.note === '' ? {} : { note: draft.note }),
                }),
              })
              .catch(() => null);

            if (response === null) {
              state.setServerError(t('error.unavailable'));
              return;
            }
            if (!response.ok) {
              const body = (await response.json().catch(() => null)) as {
                error?: { code?: string };
              } | null;
              /* ⚠️ The two refusals somebody can act on get their own sentence, as they do on the
                 time screen: one is *open the claim you already have*, the other is *an
                 administrator has to add a type*. */
              const code = body?.error?.code ?? 'unavailable';
              state.setServerError(
                code === 'already_claimed'
                  ? t('logs.overtime.error.already')
                  : code === 'no_type'
                    ? t('logs.overtime.error.noType')
                    : messageFor(t, code),
              );
              return;
            }

            setClaiming(false);
            await state.reload();
            state.setToasts((current) => [
              ...current,
              {
                id: `ot-${String(Date.now())}`,
                variant: 'success',
                title: t('logs.overtime.raised'),
              },
            ]);
          })();
        }}
      />

      <DecideDialog
        request={deciding}
        options={decisionOptions}
        serverError={state.serverError}
        onClose={() => {
          setDeciding(null);
        }}
        onConfirm={(note, draft: DecisionDraft) => {
          if (deciding === null) return;
          /* 26 Oct 2026 · closed once it lands; a refusal stays in the dialog. */
          void decide(
            state,
            session,
            t,
            deciding.selection,
            deciding.decision,
            /* ⚠️ Only an APPROVED claim settles — a rejection prices nothing, and the leave
               endpoint ignores the field either way. */
            deciding.decision === 'approved' && deciding.selection.overtimeIds.length > 0
              ? settleBody(draft)
              : undefined,
            note,
          ).then((landed) => {
            if (landed) setDeciding(null);
          });
        }}
      />

      <RequestOverlay
        requestId={openId}
        mode={openMode}
        onModeChange={setOpenMode}
        onClose={() => {
          setOpenId(null);
        }}
        onChanged={() => {
          void state.reload();
        }}
        onOpenFull={(identifier) => {
          go(`/requests/${identifier}`);
        }}
        onOpenTask={(key) => {
          go(`/tasks/${key}`);
        }}
      />
      <Toaster
        toasts={[...state.toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          state.setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </ScreenState>
  );
}

/**
 * Kérelmek — the approver's queue.
 *
 * ⚠️⚠️ **THE BULK DECISION ANSWERS PER ID, AND THE TOAST SAYS SO.** A request somebody else decided
 * while this list was open comes back refused rather than failing the batch, so *"7 decided, 1
 * could not be"* is a real outcome and not an error state.
 */
/**
 * ⚠️⚠️ **TWO QUEUES, ONE BODY — AND THEY ARE DIFFERENT QUESTIONS.** *Elbírálandó kérelmek* is what
 * THIS person must review: their reports' leave, and the overtime claims naming them as approver.
 * *Kérelmek* is every request in the workspace, behind `leave.view`. A lead needs the first whether
 * or not they hold the permission for the second, and somebody holding both needs to be able to
 * pick — which is exactly what one screen serving both could not do.
 *
 * ⚠️ One body rather than two: the columns, the drawer, the bulk decision and the toasts are the
 * same in both, and a second copy is a second place for them to drift.
 */
function QueueRoute({ listScope }: { readonly listScope: 'decide' | 'all' }) {
  const state = useRequests(listScope);
  const { t, session } = state;
  const go = useWorkspaceNavigate();
  /* ⚠️ The pictures come through the avatar cache, which fetches each path once for the
     whole app — `use-avatar.ts` argues why a hook cannot be called per row. */
  const look = usePeopleLook(state.people);
  /* ⚠️ P3-9 · a row opens the DRAWER over the list, which is how every other detail in
     the product opens (`task-overlay.tsx`). The page is still an address. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [openMode, setOpenMode] = useState<RequestMode>(openSurface);
  /* ⚠️ The batch the dialog is asking about — `null` closes it. One selection, one answer. */
  const [deciding, setDeciding] = useState<DecideRequest | null>(null);
  /* ⚠️ The same two lists the detail rail reads, from the same hook — `decision-options.ts`. */
  const decisionOptions = useDecisionOptions();

  return (
    <ScreenState failure={state.failure} onRetry={() => void state.reload()}>
      <RequestsScreen
        listScope={listScope}
        loading={state.loading}
        teams={state.teams}
        teamId={state.teamId}
        onTeam={state.setTeamId}
        /* ⚠️ The queue never offers it — this screen is about everybody else's requests. */
        canRequest={false}
        people={look}
        nonce={state.revision}
        balances={[]}
        types={state.types}
        serverError={state.serverError}
        handlers={{
          onNew: () => undefined,
          onNewClaim: () => undefined,
          onOpen: (id) => {
            setOpenId(id);
            setOpenMode(openSurface());
          },
          onOpenTask: (key) => {
            go(`/tasks/${key}`);
          },
          /**
           * ⚠️⚠️ **IT ASKS, IT DOES NOT DECIDE.** The kebab's *Elfogádás* used to post an
           * approval the instant it was clicked — no note, no settlement and, after `0147`, no
           * overtime type, which left the approver's one real choice reachable only by opening the
           * request. `decide-dialog.tsx` is the surface; this only carries the selection to it.
           */
          onDecide: (selection, decision) => {
            setDeciding({ selection, decision });
          },
          /* ⚠️ A decider does not revoke — that is the requester's act, and `canRevoke` is false
             for everybody else, so the menu never offers it here. */
          onRevoke: () => undefined,
        }}
      />

      <DecideDialog
        request={deciding}
        options={decisionOptions}
        serverError={state.serverError}
        onClose={() => {
          setDeciding(null);
        }}
        onConfirm={(note, draft: DecisionDraft) => {
          if (deciding === null) return;
          /* 26 Oct 2026 · closed once it lands; a refusal stays in the dialog. */
          void decide(
            state,
            session,
            t,
            deciding.selection,
            deciding.decision,
            /* ⚠️ Only an APPROVED claim settles — a rejection prices nothing, and the leave
               endpoint ignores the field either way. */
            deciding.decision === 'approved' && deciding.selection.overtimeIds.length > 0
              ? settleBody(draft)
              : undefined,
            note,
          ).then((landed) => {
            if (landed) setDeciding(null);
          });
        }}
      />

      <RequestOverlay
        requestId={openId}
        mode={openMode}
        onModeChange={setOpenMode}
        onClose={() => {
          setOpenId(null);
        }}
        onChanged={() => {
          void state.reload();
        }}
        onOpenFull={(identifier) => {
          go(`/requests/${identifier}`);
        }}
        onOpenTask={(key) => {
          go(`/tasks/${key}`);
        }}
      />
      <Toaster
        toasts={[...state.toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          state.setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </ScreenState>
  );
}

/** Elbírálandó kérelmek — the lead's own queue. Needs no permission: being the reviewer is it. */
export function ToDecideRoute() {
  return <QueueRoute listScope="decide" />;
}

/** Kérelmek — every request in the workspace, behind `leave.view`. */
export function ApprovalsRoute() {
  return <QueueRoute listScope="all" />;
}
